import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { genererCanauxRuban, MATCAP_GAIN, CHROME_BOX } from './lib/matcap.js';
import { vdjColor } from './lib/vdjLook.js';
import { getWoofer, preloadWoofer } from '../woofer-assets.js';

export const PROFILE = {
  id: 'woofer',
  family: 'photographic',
  danceability: 0.60,
  energyLevel: 0.86,
  bpmMin: 70,
  bpmMax: 175,
  movement: 0.75,
  tonality: 0.30,
  prefers: { lowEnd: [0.60, 1.0] },
  enabled: true
};

// Woofer : la PHOTO d'un haut-parleur de basse couché, vue inclinée, qui
// POMPE sur chaque kick (rebond amorti) ; du sable et des billes posés sur
// le cône sautent en paraboles. Bass music, dubstep, drum'n'bass, trap,
// techno. Gamme 0,60 (R9, 0,55-0,80) : impulsions de temps (la pompe, les
// LED), changements sur la phrase (la teinte de l'anneau), aucun flash plein
// écran ; au drop les LED passent au blanc.
//
// LES ASSETS (tools/build-woofer.mjs, R8) : deux photos alignées (LED
// éteintes / allumées en blanc), la lumière des LED en couche à part, les
// ellipses mesurées (Canny + RANSAC) E0 bord du saladier, E1 bord intérieur
// du saladier, E2 bord intérieur de la suspension, E3 bord du cache-noyau,
// l'inclinaison de la vue et l'AXE du haut-parleur projeté (vers le haut de
// l'image), les masques adoucis et deux coordonnées radiales : s (0 en E1,
// 1 en E2 ; 0 dehors, 1 dedans) et c (0 en E2, 1 en E3). Les reflets de la
// suspension et du cache sont RETIRÉS de la photo de base et rangés à part.
//
// LE POMPAGE déforme la photo par UN champ de déplacement continu :
//     disp(q) = s(q) x (D + 0,02 x (q - centre du cache)),  D = x . kPx . a
// (kPx = 3 % du grand axe de E2) : 0 sur le saladier, plein sur le cône et
// le cache (déplacement rigide + léger grossissement), interpolé par s sur
// la suspension, qui s'étire et se comprime. Le shader inverse le champ
// (point fixe q = p - disp(q), 4 itérations, contractant) et lit la photo
// une seule fois : aucune déchirure possible aux bords des masques.
// LES REFLETS glissent plus vite que la surface : cache 1,8 x D, suspension
// 1,5 x (s . D) ; ?wooferreflect=0 les colle à la surface, et le pompage
// paraît alors plat.
//
// x(t) : oscillateur amorti x'' = -w^2 x - 2 zeta w x' (zeta 0,35, 8 Hz),
// impulsions aux kicks, vibration continue des sous-basses ; x normalisé (1
// = un drop, ~0,4 = un kick normal).
//
// LE SABLE : 6 000 grains et 40 billes, balistique ANALYTIQUE dans le vertex
// shader (instant et force des deux derniers kicks, nombre de kicks), posés
// sur le cône PHOTOGRAPHIÉ : un grain à la coordonnée c et à l'angle phi est
// sur l'ellipse interpolée entre E2 et E3 (le modèle 3D du build, tronc de
// cône de profondeur 0,25 R2, donne l'axe et la pente ; l'interpolation des
// ellipses mesurées colle exactement à la photo, qui n'est pas strictement
// orthographique). Les sauts suivent l'axe projeté : des paraboles
// verticales à l'écran. Grains posés et ombres bougent avec le cône.
//
// CADRAGE : la photo est posée à l'échelle où le cache-noyau fait 1,3 fois
// la largeur de la pochette, centrée sur elle, puis réduite jusqu'à ce que
// le saladier entier (E0) sorte des zones d'exclusion (R6, nom de la station
// compris) et des marges de 5 %. Hors du saladier, fondu au noir pur : aucun
// bord d'image ne se voit jamais.
//
// highp : exception documentée, le shader travaille en PIXELS D'IMAGE
// (jusqu'à 1376) et inverse un déplacement sous-pixel.
//
// Debug : ?wooferx=<f> (force l'excursion), ?wooferkick=1 (un kick toutes les
// 1,6 s), ?woofermask=1 (masques colorés), ?wooferreflect=0.

const OMEGA = 2 * Math.PI * 8, ZETA = 0.35;
// x is NORMALISED: a drop peaks near 1, a full kick near 0.55, a normal one 0.4
const KICK_VEL = 42.6;
const X_MAX = 1.3;
const DROP_KICK = 2.2;
const K_PX = 0.03;                 // x the major axis of E2: the cone's travel at x = 1
const MAG = 0.02;                  // the cone and cap grow by 1 + 0.02 x
const REFL_CAP = 1.8, REFL_SUR = 1.5;
// the sand, in units of E2's semi-major axis (R2 = 1): a strong kick (1.2)
// throws the centre's sand to 25 % of R2: v0 = sqrt(2 g h) = sqrt(2 x 10.7 x 0.25)
const G = 10.7;
const SAND_V = 2.31 / 1.2;
const SAND_STRENGTH_MAX = 1.5;     // a drop's sand: ~0.39 R2, not over the frame
const BALL_R = 0.02;               // R2
const CAP_OVER_COVER = 1.3;
const NO_BPM = 120;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const LED_IDLE = 0.30;
const TIER = {
  0: { grains: 3000, balls: 20, shadows: false },
  1: { grains: 6000, balls: 40, shadows: true },
  2: { grains: 6000, balls: 40, shadows: true }
};
const ACCENT_FALLBACK = [0x2bd6ff, 0xff2bd6, 0xffb02b];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// ---------------------------------------------------------------------------
const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

const photoFragment = `
  precision highp float;
  uniform sampler2D uBase;
  uniform sampler2D uLight;
  uniform sampler2D uMasks;
  uniform sampler2D uFields;
  uniform vec2 uImg;
  uniform vec2 uOrigin;           // image px at the screen's centre-origin (height units)
  uniform float uK;               // image px per height unit
  uniform float uAspect;
  uniform vec2 uD;                // the cone's rigid displacement, image px
  uniform float uMag;             // the cone's growth (0.02 x)
  uniform vec2 uCc;               // the cap's centre
  uniform float uX;
  uniform float uReflect;         // 1: glints slide faster than the surface
  uniform float uLed;
  uniform float uWhite;
  uniform vec3 uTint;
  uniform vec4 uE0;               // cx, cy, a, b
  uniform vec2 uE0r;              // cos, sin of its angle
  uniform float uMaskView;
  varying vec2 vUv;

  vec2 st(vec2 q) { return vec2(q.x / uImg.x, 1.0 - q.y / uImg.y); }
  float rhoE0(vec2 p) {
    vec2 d = p - uE0.xy;
    vec2 u = vec2(d.x * uE0r.x + d.y * uE0r.y, -d.x * uE0r.y + d.y * uE0r.x);
    return length(u / uE0.zw);
  }
  void main() {
    vec2 h = vec2((vUv.x - 0.5) * 2.0 * uAspect, (vUv.y - 0.5) * 2.0);
    vec2 p = uOrigin + vec2(h.x, -h.y) * uK;
    // the inverse of q -> q + s(q) (D + m (q - Cc)): a fixed point, contracting
    vec2 q = p;
    for (int i = 0; i < 4; i++) {
      float s0 = texture2D(uFields, st(q)).r;
      q = p - s0 * (uD + uMag * (q - uCc));
    }
    vec3 f = texture2D(uFields, st(q)).rgb;
    vec3 m = texture2D(uMasks, st(q)).rgb;            // surround, cone, cap
    vec3 col = texture2D(uBase, st(q)).rgb;
    float s = f.r;
    // the glints, back on, sliding faster than the surface
    vec2 qc = q - uReflect * ${(REFL_CAP - 1).toFixed(2)} * uD;
    vec2 qs = q - uReflect * ${(REFL_SUR - 1).toFixed(2)} * s * uD;
    float gc = texture2D(uFields, st(qc)).b; gc *= gc;
    float gs = texture2D(uFields, st(qs)).b; gs *= gs;
    col += vec3(gc * m.b + gs * m.r);
    // the surround brightens a little when it is stretched forward
    col *= 1.0 + 0.9 * max(uX, 0.0) * m.r * s * (1.0 - s);
    // the cone going back: a light darkening at its bottom edge, near E2
    col *= 1.0 - 0.15 * clamp(-uX, 0.0, 1.0) * m.g * (1.0 - smoothstep(0.0, 0.45, f.g));
    // the LED ring: white light x intensity x the accent (luminance kept)
    float lt = texture2D(uLight, st(q)).r;
    col += lt * uLed * mix(uTint, vec3(1.0), uWhite);
    if (uMaskView > 0.5) col = col * 0.35 + vec3(m.r, m.g * 0.6 + s * 0.4 * m.r, m.b) * 0.45;
    // black around the basket: the picture's edges never show (R3)
    col *= 1.0 - smoothstep(1.004, 1.05, rhoE0(p));
    if (p.x < 0.0 || p.y < 0.0 || p.x > uImg.x || p.y > uImg.y) col = vec3(0.0);
    gl_FragColor = vec4(col, 1.0);
  }
`;

// --- the sand: shared ballistics on the photographed cone -----------------------
// aSeed: x = initial radial fraction, y = angle (0..1), z = radial step per
// jump, w = a random for the launch.
const SAND_COMMON = `
  precision highp float;
  attribute vec4 aSeed;
  uniform vec4 uKick;             // last kick time-since, its strength, previous time-since, its strength
  uniform float uKickN;
  uniform float uTremble;
  uniform float uTime;
  uniform vec4 uE2; uniform vec2 uE2r;
  uniform vec4 uE3; uniform vec2 uE3r;
  uniform vec2 uAxis;             // the projected axis, up the picture
  uniform float uHpx;             // image px per unit of height along the axis
  uniform vec2 uD;
  uniform float uMag;
  uniform vec2 uCc;
  uniform vec2 uOrigin;
  uniform float uK;
  uniform float uAspect;
  uniform float uPx;              // screen px per "pixel" (1 at 700 px tall)
  uniform float uImgPx;           // screen px per image px
  uniform vec2 uShadowDir;        // image px of shadow offset per unit of height
  float hashf(float n) { n = fract(n * 0.1031); n *= n + 33.33; n *= n + n; return fract(n); }
  // the cone coordinate after k jumps: each jump moves the grain a step DOWN
  // the slope (toward the cap); it reappears near the rim. u^1.7: denser at
  // the bottom
  float coneAt(vec4 s, float k) {
    float u = pow(fract(s.x - k * s.z), 1.7);
    return 0.96 - 0.90 * u;
  }
  // launch speed: strongest at the centre (the bottom of the cone)
  float launchV(float c, float strength, float rnd, float mass) {
    return strength * (0.35 + 0.65 * pow(c, 1.3)) * (0.75 + 0.5 * rnd) * mass;
  }
  // a point of the photographed cone: the ellipse between E2 and E3
  vec2 conePt(float c, float phi) {
    vec4 e = mix(uE2, uE3, c);
    vec2 r = normalize(mix(uE2r, uE3r, c));
    vec2 uv = vec2(e.z * cos(phi), e.w * sin(phi));
    return e.xy + vec2(uv.x * r.x - uv.y * r.y, uv.x * r.y + uv.y * r.x);
  }
  // the grain's resting point (image px, moving with the cone) and height
  vec3 grain(vec4 s, float mass) {
    float kLast = uKickN, kPrev = uKickN - 1.0;
    float cP = coneAt(s, kPrev - 1.0), cPn = coneAt(s, kPrev);
    float vP = launchV(cP, uKick.w, hashf(s.w * 91.0 + kPrev), mass);
    float tP = uKick.z, flightP = 2.0 * vP / ${G.toFixed(2)};
    float gap = uKick.z - uKick.x;
    float useP = step(gap, flightP) * step(tP, flightP);
    float c0, c1, v, t, k;
    if (useP > 0.5) { c0 = cP; c1 = cPn; v = vP; t = tP; k = kPrev; }
    else {
      c0 = coneAt(s, kLast - 1.0); c1 = coneAt(s, kLast);
      v = launchV(c0, uKick.y, hashf(s.w * 91.0 + kLast), mass); t = uKick.x; k = kLast;
    }
    float flight = 2.0 * v / ${G.toFixed(2)};
    float f = flight > 1e-4 ? clamp(t / flight, 0.0, 1.0) : 1.0;
    float c = mix(c0, c1, f);
    if (c1 < c0) c = f < 0.5 ? c0 : c1;              // wrapped: reappear at the rim on landing
    // a ball rolls a little down the slope once it has landed
    if (mass < 0.99) c = min(0.97, c + 0.025 * clamp(t - flight, 0.0, 2.5));
    float phi = (s.y + 0.02 * (hashf(s.w * 17.0 + k) - 0.5)) * 6.2831853;
    float h = max(v * t - 0.5 * ${G.toFixed(2)} * t * t, 0.0);
    h += uTremble * 0.0086 * (0.5 + 0.5 * sin(uTime * (40.0 + 25.0 * s.w) + s.w * 50.0));
    vec2 p = conePt(c, phi);
    p += uD + uMag * (p - uCc);                       // the cone pumps: the sand rides it
    return vec3(p, h);
  }
  vec4 toClip(vec2 p) {
    vec2 hh = vec2((p.x - uOrigin.x) / uK, -(p.y - uOrigin.y) / uK);
    return vec4(hh.x / uAspect, hh.y, 0.0, 1.0);
  }
`;

const grainVertex = `
  ${SAND_COMMON}
  uniform float uMass;
  uniform float uShadow;
  varying float vH;
  varying float vRnd;
  varying float vSize;
  void main() {
    vec3 g = grain(aSeed, uMass);
    vH = g.z; vRnd = aSeed.w;
    vec2 p = g.xy;
    float size;
    bool ball = uMass < 0.99;
    float ballPx = 2.0 * ${BALL_R.toFixed(3)} * uE2.z * uImgPx;
    if (uShadow > 0.5) {
      // on the cone, under the grain: offset away from the light (top left)
      // and softer as it flies
      p += uShadowDir * g.z;
      size = ball ? ballPx * (1.25 + 2.5 * g.z) : uPx * (2.2 + 18.0 * g.z);
    } else {
      p += uAxis * g.z * uHpx;                         // the jump, along the axis
      if (ball) p += uAxis * ${BALL_R.toFixed(3)} * uHpx;   // a ball sits ON the cone
      size = ball ? ballPx : uPx * (1.0 + 0.9 * aSeed.w);   // grains 1 to 2 px (R7)
    }
    vSize = size;
    gl_Position = toClip(p);
    gl_PointSize = size;
  }
`;
const grainFragment = `
  precision highp float;
  uniform float uShadow;
  uniform vec3 uSand;
  varying float vH;
  varying float vRnd;
  varying float vSize;
  void main() {
    vec2 d = gl_PointCoord - 0.5;
    float r = length(d) * 2.0;
    if (uShadow > 0.5) {
      // multiply: 1 = untouched; a resting grain darkens its contact, a flying
      // one less and wider
      float a = (1.0 - smoothstep(0.15, 1.0, r)) * 0.55 / (1.0 + vH * 25.0);
      gl_FragColor = vec4(vec3(1.0 - a), 1.0);
      return;
    }
    if (r > 1.0) discard;
    // light sand lit from the top left, as the photograph
    float lit = 0.8 + 0.4 * clamp(-d.x - d.y + 0.2, 0.0, 1.0);
    float a = 1.0 - smoothstep(0.55, 1.0, r);
    gl_FragColor = vec4(uSand * (0.55 + 0.45 * vRnd) * lit * a, 1.0);
  }
`;
// the chrome balls: impostor spheres, the normal from the sprite, the same
// chrome matcap as the rest of the engine (R5), its highlight turned to the
// photograph's light (top left)
const ballFragment = `
  precision highp float;
  uniform sampler2D uMat;
  uniform float uShadow;
  varying float vH;
  varying float vRnd;
  varying float vSize;
  void main() {
    vec2 d = gl_PointCoord * 2.0 - 1.0;
    d.y = -d.y;
    float r2 = dot(d, d);
    if (uShadow > 0.5) {
      float r = sqrt(r2);
      float a = (1.0 - smoothstep(0.25, 1.0, r)) * 0.6 / (1.0 + vH * 12.0);
      gl_FragColor = vec4(vec3(1.0 - a), 1.0);
      return;
    }
    if (r2 > 1.0) discard;
    vec3 n = vec3(d, sqrt(1.0 - r2));
    vec4 mc = texture2D(uMat, n.xy * 0.49 + 0.5);
    float body = mc.b * mc.b * ${MATCAP_GAIN.toFixed(2)}, box = mc.a * ${CHROME_BOX.toFixed(2)};
    vec3 col = vec3(0.72, 0.74, 0.78) * body + vec3(box) * 0.9;
    // the edge: one pixel of coverage, no stair-step
    float edge = 1.0 - smoothstep(1.0 - 2.0 / max(vSize, 2.0), 1.0, sqrt(r2));
    gl_FragColor = vec4(col, edge);
  }
`;

const MATS = new Map();
let UNI = null;
let MATCAP_TEX = null;
function matcapTex() {
  if (MATCAP_TEX) return MATCAP_TEX;
  const m = genererCanauxRuban(256);
  const t = new THREE.DataTexture(m.data, m.size, m.size, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.colorSpace = THREE.NoColorSpace; t.minFilter = t.magFilter = THREE.LinearFilter;
  t.generateMipmaps = false; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; t.needsUpdate = true;
  MATCAP_TEX = t;
  return t;
}
function luma(c) { return 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b; }

export class WooferVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    const fx = parseFloat(urlParam('wooferx'));
    this._forceX = Number.isFinite(fx) ? fx : null;
    this._debugKick = urlParam('wooferkick') === '1';
    this._maskView = urlParam('woofermask') === '1';
    this._reflect = urlParam('wooferreflect') === '0' ? 0 : 1;
    // a photograph: the bloom only for the LED's core and the white drop
    this._post = { bloomStrength: 0.25, bloomThreshold: 0.92, vignette: 0.30, grain: 0.010, aberration: 0.0006, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 };
  }

  get postProcessing() { return this._post; }

  init(scene, camera) {
    this._scene = scene; this._camera = camera;
    const assets = getWoofer();
    this._ready = !!assets;
    if (!assets) {
      // the photographs are still loading (or missing: the entry point then
      // withdraws Woofer): retry once they are in
      const self = this;
      preloadWoofer().then(function (ok) {
        if (!ok || !self._scene || self._ready) return;
        const s = self._scene, c = self._camera;
        self.dispose(); self.init(s, c);
      });
      return;
    }
    const d = assets.data;
    this._data = d;
    const E = d.ellipses;
    const e4 = (e) => new THREE.Vector4(e.cx, e.cy, e.a, e.b);
    const r2 = (e) => new THREE.Vector2(Math.cos(e.theta), Math.sin(e.theta));
    // shared uniforms: ONE module-level object, because the materials are
    // cached across instances and three.js keeps the uniform object a
    // material was first compiled with (a fresh one would freeze the second
    // appearance)
    if (!UNI) UNI = {
      uBase: { value: null }, uLight: { value: null }, uMasks: { value: null }, uFields: { value: null }, uMat: { value: matcapTex() },
      uImg: { value: new THREE.Vector2() }, uOrigin: { value: new THREE.Vector2() }, uK: { value: 1 }, uAspect: { value: 16 / 9 },
      uD: { value: new THREE.Vector2() }, uMag: { value: 0 }, uCc: { value: new THREE.Vector2() }, uX: { value: 0 },
      uReflect: { value: 1 }, uLed: { value: LED_IDLE }, uWhite: { value: 0 }, uTint: { value: new THREE.Color(1, 1, 1) },
      uE0: { value: new THREE.Vector4() }, uE0r: { value: new THREE.Vector2(1, 0) }, uMaskView: { value: 0 },
      uE2: { value: new THREE.Vector4() }, uE2r: { value: new THREE.Vector2(1, 0) }, uE3: { value: new THREE.Vector4() }, uE3r: { value: new THREE.Vector2(1, 0) },
      uAxis: { value: new THREE.Vector2(0, -1) }, uHpx: { value: 1 },
      uKick: { value: new THREE.Vector4(10, 0, 20, 0) }, uKickN: { value: 0 }, uTremble: { value: 0 }, uTime: { value: 0 },
      uPx: { value: 1 }, uImgPx: { value: 1 }, uShadowDir: { value: new THREE.Vector2() },
      uSand: { value: new THREE.Color(0.62, 0.52, 0.36) }
    };
    const u = this._u = UNI;
    u.uBase.value = assets.base; u.uLight.value = assets.light; u.uMasks.value = assets.masks; u.uFields.value = assets.fields;
    u.uImg.value.set(d.size[0], d.size[1]);
    u.uE0.value.copy(e4(E.E0)); u.uE0r.value.copy(r2(E.E0));
    u.uE2.value.copy(e4(E.E2)); u.uE2r.value.copy(r2(E.E2));
    u.uE3.value.copy(e4(E.E3)); u.uE3r.value.copy(r2(E.E3));
    u.uCc.value.set(E.E3.cx, E.E3.cy);
    u.uAxis.value.set(d.axis[0], d.axis[1]);
    u.uHpx.value = d.pxPerUnit * Math.sin(d.tilt);
    // light from the top left: a shadow falls down and to the right
    u.uShadowDir.value.set(0.42 * d.pxPerUnit, 0.30 * d.pxPerUnit);
    u.uMaskView.value = this._maskView ? 1 : 0;
    u.uReflect.value = this._reflect;
    this._kPx = K_PX * 2 * E.E2.a;

    const L = this._layout;
    const mat = (key, make) => { let m = MATS.get(key); if (!m) { m = make(); MATS.set(key, m); } return m; };
    // the photograph
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat('photo', () => new THREE.ShaderMaterial({
      uniforms: u, vertexShader: quadVertex, fragmentShader: photoFragment, depthTest: false, depthWrite: false })));
    this._quad.frustumCulled = false; this._quad.renderOrder = -3000;
    const self = this;
    this._size = new THREE.Vector2();
    this._quad.onBeforeRender = function (renderer) {
      renderer.getDrawingBufferSize(self._size);
      self._reframe();
      u.uPx.value = Math.max(1.0, self._size.y / 700);
      u.uImgPx.value = (self._size.y / 2) / u.uK.value;
    };
    scene.add(this._quad);

    // the sand and the balls: Points, clip-space positions from the shader
    let seed = 1234567;
    const rnd = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
    const pointsGeo = (n, step0, step1) => {
      const seeds = new Float32Array(n * 4);
      for (let i = 0; i < n; i++) { seeds[i * 4] = rnd(); seeds[i * 4 + 1] = rnd(); seeds[i * 4 + 2] = step0 + step1 * rnd(); seeds[i * 4 + 3] = rnd(); }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
      g.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 4));
      return g;
    };
    this._grainGeo = pointsGeo(L.grains, 0.006, 0.03);
    this._ballGeo = pointsGeo(L.balls, 0.015, 0.04);
    const pointsMat = (kind, shadow) => mat(kind + shadow, () => new THREE.ShaderMaterial({
      uniforms: Object.assign({ uShadow: { value: shadow }, uMass: { value: kind === 'ball' ? 0.55 : 1.0 } }, u),
      vertexShader: grainVertex, fragmentShader: kind === 'ball' ? ballFragment : grainFragment,
      depthTest: false, depthWrite: false,
      transparent: shadow === 1 || kind === 'ball',
      blending: shadow === 1 ? THREE.MultiplyBlending : THREE.NormalBlending, premultipliedAlpha: shadow === 1
    }));
    this._points = [];
    const add = (geo, kind, shadow, order) => {
      const p = new THREE.Points(geo, pointsMat(kind, shadow));
      p.frustumCulled = false; p.renderOrder = order;
      scene.add(p); this._points.push(p);
    };
    if (L.shadows) { add(this._grainGeo, 'grain', 1, -2990); add(this._ballGeo, 'ball', 1, -2989); }
    add(this._grainGeo, 'grain', 0, -2980);
    add(this._ballGeo, 'ball', 0, -2979);

    // state
    this._time = 0; this._x = 0; this._v = 0;
    this._kickAt = -10; this._kickV = 0; this._prevKickAt = -20; this._prevKickV = 0; this._kickN = 0;
    this._phaseBeat = null; this._phasePhrase = null; this._free = 0;
    this._led = LED_IDLE; this._ledWhite = 0; this._ledFullUntil = -1;
    this._accentIdx = 0; this._accents = ACCENT_FALLBACK.map((c) => vdjColor(c));
    this._paletteSeen = undefined;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._lowAvg = 0; this._build = 0;
    this._setTint(this._accents[0]);
    this._frameKey = null;
    this._reframe();
    this._applyX(this._forceX != null ? this._forceX : 0);
  }

  // the accent applied by multiplication, luminance kept (R4): the white LED
  // light keeps its brightness and takes the hue. A dark hue (blue) would
  // need x14 on one channel: the gain is capped (no channel over 2.2, or the
  // bloom spills a halo past the basket onto the black), it loses some light
  _setTint(c) {
    const k = Math.min(1 / Math.max(luma(c), 0.3), 2.2 / Math.max(c.r, c.g, c.b, 1e-3));
    this._u.uTint.value.setRGB(c.r * k, c.g * k, c.b * k);
  }

  // the exclusion zones, in NDC (x, y in -1..1): R6 + the station name
  _excluded(x, y) {
    if (Math.abs(x) > 0.90 || Math.abs(y) > 0.90) return true;            // 5 % margins
    if (y > 0.80 && Math.abs(x) < 0.40) return true;                     // station name, top centre 40 % x 10 %
    if (y > 0.76 && x < -0.84) return true;                              // microphone, top left 8 % x 12 %
    if (y > 0.76 && x > 0.56) return true;                               // Broadcast, top right 22 % x 12 %
    if (y < -0.80 && x > 0.56) return true;                              // navigation, bottom right 22 % x 10 %
    return false;
  }

  // the framing: the cap under the cover at 1.3 x its width, then smaller
  // until the whole basket clears the margins and the interface
  _reframe() {
    const aspect = (this._size && this._size.y > 0) ? this._size.x / this._size.y : (safeZoneUniforms.uAspect.value || 16 / 9);
    // R19: framed on the centre of composition, not on the cover
    const sc = safeZoneUniforms.uStageCenter.value, hs = safeZoneUniforms.uStageHalfSize.value;
    const key = aspect.toFixed(4) + ':' + sc.x.toFixed(3) + ',' + sc.y.toFixed(3) + ':' + hs.x.toFixed(3) + ',' + hs.y.toFixed(3);
    if (key === this._frameKey) return;
    this._frameKey = key;
    const E = this._data.ellipses, u = this._u;
    const Tx = sc.x, Ty = sc.y + Math.max(0, hs.y - hs.x);          // the cover's square, at the top of the overlay
    const cc = [E.E3.cx, E.E3.cy];
    const rim = [];
    const e0 = E.E0, ct = Math.cos(e0.theta), st = Math.sin(e0.theta);
    // E0 x 1.05: the fade to black past the rim (rho 1.004 to 1.05) still shows
    for (let i = 0; i < 96; i++) {
      const t = i / 96 * Math.PI * 2, ux = 1.05 * e0.a * Math.cos(t), vy = 1.05 * e0.b * Math.sin(t);
      rim.push([e0.cx + ux * ct - vy * st, e0.cy + ux * st + vy * ct]);
    }
    const fits = (k) => {
      for (const [px, py] of rim) {
        const X = (px - cc[0]) / k + Tx, Y = -(py - cc[1]) / k + Ty;
        if (this._excluded(X / aspect, Y)) return false;
      }
      return true;
    };
    const kWant = hs.x > 0 ? E.E3.a / (CAP_OVER_COVER * hs.x) : 300;
    let k = kWant;
    if (!fits(k)) {
      let lo = kWant, hi = kWant * 8;
      for (let i = 0; i < 30; i++) { const m = 0.5 * (lo + hi); if (fits(m)) hi = m; else lo = m; }
      k = hi;
    }
    u.uK.value = k; u.uAspect.value = aspect;
    u.uOrigin.value.set(cc[0] - Tx * k, cc[1] + Ty * k);
    // for the report: the cap's width over the cover's, the basket's width
    this._capOverCover = hs.x > 0 ? E.E3.a / (k * hs.x) : null;
    this._basketWidth = (2 * E.E0.a / k) / (2 * aspect);
  }

  _applyPalette(profile) {
    if (profile.artworkPalette === this._paletteSeen) return;
    this._paletteSeen = profile.artworkPalette;
    const v = profile.artworkPalette && profile.artworkPalette.vividColors && profile.artworkPalette.vividColors.length ? profile.artworkPalette.vividColors : ACCENT_FALLBACK;
    this._accents = v.slice(0, 4).map((c) => vdjColor(c));
    this._setTint(this._accents[this._accentIdx % this._accents.length]);
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  // a kick: an impulse into the membrane, and a launch for the sand
  _kick(strength) {
    this._v += KICK_VEL * strength;
    this._prevKickAt = this._kickAt; this._prevKickV = this._kickV;
    this._kickAt = this._time; this._kickV = SAND_V * Math.min(strength, SAND_STRENGTH_MAX);
    this._kickN++;
  }

  onTrackChange(profile) { if (!this._ready) return; this._applyPalette(profile); this._dropIdx = 0; this._lastNowMs = null; }

  update(deltaTime, profile) {
    if (!this._ready) return;
    // pause: everything holds - the membrane, the sand in the air
    if (profile.playbackState !== 'PLAYING') return;
    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;
    const t = this._time;
    this._applyPalette(profile);
    const director = profile.director;
    const bands = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    const energy = director && director.intensity != null ? clamp01(director.intensity) : 0.6;
    const low = bands ? clamp01(bands.low) : energy * 0.8;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, ph = profile.phrasePhase;
    const grid = bp != null && bpm > 0;
    const beatS = 60 / (grid ? bpm : NO_BPM);
    let beat = false, phrase = false;
    if (grid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true; this._phaseBeat = bp;
      if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    } else {
      // no BPM: a soft pump at 120
      this._free += dt; if (this._free >= beatS) { this._free -= beatS; beat = true; this._noBpmBeats = (this._noBpmBeats || 0) + 1; if (this._noBpmBeats % 32 === 0) phrase = true; }
    }

    // the build: a drop coming - the membrane shivers, the sand trembles
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.5);
    this._build += (build - this._build) * (1 - Math.exp(-dt / 0.5));

    // KICK: the beats, scaled by the low end (no low end: a soft pump)
    if (beat) {
      const strength = 0.45 + 0.75 * low;
      this._kick(this._build > 0.3 ? strength * 0.25 : strength);   // in the build: shiver, no jump
      this._led = Math.max(this._led, 0.6 + 0.3 * low);
    }
    if (this._debugKick && t - (this._lastDebugKick || -9) > 1.6) { this._lastDebugKick = t; this._kick(1.0); this._led = Math.max(this._led, 0.8); }
    // DROP: the biggest kick, the LEDs full and white for a beat (no flash: R9)
    if (grid && this._tickDrops(profile)) {
      this._kick(DROP_KICK);
      this._ledWhite = 1;
      this._ledFullUntil = t + beatS;
    }
    // PHRASE: the ring's next colour
    if (phrase) {
      this._accentIdx++;
      this._setTint(this._accents[this._accentIdx % this._accents.length]);
    }

    // --- the membrane: x'' = -w^2 x - 2 zeta w x' (+ impulses, above)
    const steps = 6, h = dt / steps;
    for (let s = 0; s < steps; s++) {
      const a = -OMEGA * OMEGA * this._x - 2 * ZETA * OMEGA * this._v;
      this._v += a * h; this._x += this._v * h;
    }
    // the sustained sub: a small continuous vibration, more in the build
    this._lowAvg += (low - this._lowAvg) * (1 - Math.exp(-dt / 0.6));
    const sub = (0.023 * this._lowAvg + 0.056 * this._build) * Math.sin(t * 2 * Math.PI * 11.0);
    let x = Math.max(-X_MAX, Math.min(X_MAX, this._x + sub));
    if (this._forceX != null) x = this._forceX;
    this._applyX(x);

    // --- sand: only the two last kicks' times and strengths, and the count
    const u = this._u;
    u.uKick.value.set(t - this._kickAt, this._kickV, t - this._prevKickAt, this._prevKickV);
    u.uKickN.value = this._kickN;
    u.uTremble.value = this._build;
    u.uTime.value = t;

    // --- the ring: pulses on the beat, full and white on a drop
    this._led = Math.max(LED_IDLE, LED_IDLE + (this._led - LED_IDLE) * Math.exp(-dt / 0.18));
    if (t < this._ledFullUntil) this._led = 1;
    this._ledWhite *= Math.exp(-dt / 0.5);
    u.uLed.value = this._led;
    u.uWhite.value = this._ledWhite;
  }

  _applyX(x) {
    const u = this._u, a = this._data.axis;
    u.uX.value = x;
    u.uD.value.set(a[0] * x * this._kPx, a[1] * x * this._kPx);
    u.uMag.value = MAG * x;
  }

  dispose() {
    if (this._quad) {
      this._quad.onBeforeRender = null;
      if (this._scene) this._scene.remove(this._quad);
      this._quad.geometry.dispose();
      this._quad = null;
    }
    for (const p of this._points || []) if (this._scene) this._scene.remove(p);
    this._points = [];
    if (this._grainGeo) { this._grainGeo.dispose(); this._grainGeo = null; }
    if (this._ballGeo) { this._ballGeo.dispose(); this._ballGeo = null; }
    this._scene = null;
  }
}
